import {
  ItemSearchViewUI,
  ModalCWShellUI,
} from '@next-order/next-order-components';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { isMobile } from '../../helpers/isMobile';
import { eventNames } from 'configuration/eventNames';
import { MenuContext } from 'contexts/MenuProvider';
import getImage from 'helpers/getImage';

const glutenFreeVariants = [
  'gluten',
  'gluten free',
  'gluten-free',
  'glutenfree',
  'gf',
];
// const dairyFreeVariants = ['dairy free', 'dairy-free', 'dairyfree', 'df'];
// const veganVariants = ['vegan', 'vegitarian', 'no meat', 'veg', 'veggie'];

export default function SearchModal({
  searchCategoriesList,
  onClickItemHandler,
}) {
  const { searchModalOpen, setSearchModalOpen, setHighlightedItemId } =
    useContext(MenuContext);

  const [search, setSearch] = useState('');

  const allData = useMemo(() => {
    if (!search) return [];
    const searchLowerCase = search.toLowerCase();

    return searchCategoriesList
      .map((category) => ({
        name: category.name,
        data:
          // condition 1: if category name matches search, return all items in category
          category.name.toLowerCase().includes(searchLowerCase)
            ? category.data
            : // condition 2: else return items that match search
              category.data.filter(
                (menuItem) =>
                  // condition 3: check if search matches menuItem name
                  menuItem.name.toLowerCase().includes(searchLowerCase) ||
                  // condition 4: check if search matches menuItem size name
                  menuItem.selectedSizes?.some(
                    (size) =>
                      size.name.toLowerCase().includes(searchLowerCase) ||
                      // condition 5: check if search is for gluten free, dairy free, or vegan
                      (size.isGlutenFree &&
                        glutenFreeVariants.includes(searchLowerCase)) ||
                      // condition 6: check if search matches menuItem size variant name
                      size.variants?.some((variant) =>
                        variant.name.toLowerCase().includes(searchLowerCase)
                      )
                  )
              ),
      }))
      .filter((category) => category.data.length > 0);
  }, [searchCategoriesList, search]);

  const onClose = () => {
    setSearch('');
    setSearchModalOpen(false);
  };

  const onChooseResultItem = (menuItem) => {
    onClickItemHandler(
      menuItem.itemObjType,
      menuItem,
      eventNames.SEARCH_MENU_ITEM_OPENED
    );

    onClose();

    setTimeout(() => {
      const itemElement = document.querySelector(`[data-menu-item-id="${menuItem._id}"]`);

      if (itemElement) {

        setHighlightedItemId(menuItem._id);

        itemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        setTimeout(() => {
          setHighlightedItemId(null);
        }, 3000);
      }
    }, 300);
  };

  return (
    <ModalCWShellUI
      maxWidth={460}
      open={searchModalOpen}
      onOpenChange={onClose}
      maxHeight='full'
      zIndex={100}
      extendedClassName={classNames(
        '-sm:!rounded-none -sm:!max-w-full -sm:!h-full',
        {
          'sm:!rounded-none sm:!max-w-full sm:!h-full sm:!max-h-full sm:!my-0 sm:!w-full':
            isMobile(),
          ' sm:!bg-transparent': !search && isMobile(),
          ' !bg-transparent': !search,
        }
      )}
    >
      <VisualViewport className='flex flex-col h-full'>
        <ModalCWShellUI.CloseButton onClick={onClose} />
        <ItemSearchViewUI
          autoFocus={true}
          autoComplete='off'
          extendedClassName={classNames(
            'pt-18 sm:pt-13.5 flex flex-col h-full overflow-hidden',
            {
              'sm:!bg-transparent sm:h-full': !search && isMobile(),
            }
          )}
          extendedHeaderClassName={classNames({
            'sm:hidden': !search && isMobile(),
          })}
          s={search}
          placeholder='Search Menu'
          onChangeSearch={(e) => setSearch(e.target.value)}
          onClickResetSearch={(e) => setSearch('')}
          extendedScrollableBoxClassName={classNames('flex-1 overflow-y-auto', {
            'max-h-[calc(100vh-270px)]': !isMobile(),
            'max-h-[calc(100vh-190px)]': isMobile(),
          })}
        >
          {search && allData?.length > 0 ? (
            allData.map((category, index) => (
              <ItemSearchViewUI.ResultGroup title={category.name} key={index}>
                {category.data.map((menuItem, i) => (
                  <ItemSearchViewUI.ResultItem
                    key={i + index + 100000}
                    imgSrc={getImage(menuItem.mediaPath || menuItem.urlS3)}
                    title={menuItem.name}
                    onClick={() => onChooseResultItem(menuItem)}
                  />
                ))}
              </ItemSearchViewUI.ResultGroup>
            ))
          ) : search ? (
            <ItemSearchViewUI.NoResult>Not Found</ItemSearchViewUI.NoResult>
          ) : null}
        </ItemSearchViewUI>
      </VisualViewport>
    </ModalCWShellUI>
  );
}

const VisualViewport = ({
  as: Element = 'div',
  children,
  style = {},
  ...props
}) => {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const vv = window.visualViewport;
    const node = ref.current;
    if (!vv || !node) return;

    let frame = null;

    const apply = () => {
      frame = null;
      node.style.maxHeight = `${vv.height}px`;
      node.style.maxWidth = `${vv.width}px`;
      // The modal is position:fixed, so it is anchored to the LAYOUT viewport.
      // iOS leaves the layout viewport at full height when the keyboard opens
      // and shrinks/offsets the VISUAL viewport instead, so without this the
      // content sits above the visible region with a gap over the keyboard.
      node.style.transform = vv.offsetTop
        ? `translateY(${vv.offsetTop}px)`
        : '';
    };

    // One update per frame: the keyboard animates over several hundred ms, and
    // a trailing debounce latches whichever value the browser reported last.
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(apply);
    };

    apply();

    vv.addEventListener('resize', schedule);
    // offsetTop changes fire 'scroll', not 'resize'. Listening only to 'resize'
    // leaves the content wherever it was when the keyboard finished animating.
    vv.addEventListener('scroll', schedule);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
    };
  }, []);

  return (
    <Element
      ref={ref}
      style={{ maxHeight: '100%', maxWidth: '100%', ...style }}
      {...props}
    >
      {children}
    </Element>
  );
};
